import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import BackButton from "../Button/BackButton";
import { Loader } from "../Common/Loader";
import { STATUS_BEGIN } from "utils/constants/others";
import { LS_KEYS } from "utils/constants/localStorageKeys";
import usePayoutsStore from "./usePayoutsStore";
import { CopyButton, EmptyState } from "./styles.payouts.js";
import {
  isValidDate,
  formatFullDate,
  formatMoney,
  copyToClipboard,
  CopyIcon,
  withDividers,
} from "./payoutsFormat.js";
import {
  Header,
  BackWrap,
  HeaderTitle,
  Page,
  Card,
  Divider,
  InfoRow,
  InfoValue,
  HistoryTitle,
  HistoryItem,
  HistoryDate,
  HistoryReason,
  SummaryCard,
  TagRow,
  Pill,
  SummaryHeader,
  SummaryDate,
  SummaryCount,
  SummaryAmount,
  TxnList,
  TxnRow,
  TxnTitle,
  TxnId,
  TxnAmount,
} from "./styles.paymentDetails.js";

const InfoIcon = () => (
  <svg width="12" height="12" viewBox="32 331 12 12" fill="none" aria-hidden="true">
    <path
      d="M38 331C34.6865 331 32 333.687 32 337C32 340.313 34.6865 343 38 343C41.3135 343 44 340.313 44 337C44 333.687 41.3135 331 38 331ZM38 342.148C35.157 342.148 32.852 339.843 32.852 337C32.852 334.157 35.157 331.852 38 331.852C40.843 331.852 43.148 334.157 43.148 337C43.148 339.843 40.843 342.148 38 342.148ZM38.852 340C38.852 340.47 38.4705 340.852 38 340.852C37.5295 340.852 37.148 340.47 37.148 340C37.148 339.53 37.5295 339.148 38 339.148C38.4705 339.148 38.852 339.53 38.852 340ZM38.852 334.144V337.432C38.852 337.903 38.4705 338.284 38 338.284C37.5295 338.284 37.148 337.903 37.148 337.432V334.144C37.148 333.674 37.5295 333.292 38 333.292C38.4705 333.292 38.852 333.674 38.852 334.144V334.144Z"
      fill="currentColor"
    />
  </svg>
);

const historyEntries = (history) =>
  history.map((entry, idx) => (
    <HistoryItem key={`${entry.date_range}-${idx}`}>
      <HistoryDate>{entry.date_range}</HistoryDate>
      {entry.reason ? (
        <HistoryReason>
          <InfoIcon />
          <span>{entry.reason}</span>
        </HistoryReason>
      ) : null}
    </HistoryItem>
  ));

const TO_ENTITY_TYPE = "VENDOR";

const PaymentDetails = () => {
  const [searchParams] = useSearchParams();
  const referenceNumber = searchParams.get("reference_number");
  const toEntityId =
    (typeof window !== "undefined" &&
      window.localStorage.getItem(LS_KEYS.OUTLET_ID)) ||
    "";

  const {
    accrualDetails: details,
    accrualDetailsStatus,
    getAccrualDetails,
    resetAccrualDetails,
  } = usePayoutsStore();

  // The store is shared, so drop whatever a previous payment left behind
  // (also invalidates an in-flight request) when the reference changes or on unmount.
  useEffect(() => {
    if (referenceNumber) {
      getAccrualDetails({
        reference_number: referenceNumber,
        to_entity_id: toEntityId,
        to_entity_type: TO_ENTITY_TYPE,
      });
    } else {
      resetAccrualDetails();
    }
    return () => resetAccrualDetails();
  }, [referenceNumber, toEntityId, getAccrualDetails, resetAccrualDetails]);

  const loading = accrualDetailsStatus === STATUS_BEGIN;

  const renderContent = () => {
    if (loading) return <Loader />;
    if (!details) {
      return <EmptyState>Unable to load payment details</EmptyState>;
    }

    const history = Array.isArray(details.history) ? details.history : [];
    const transactions = Array.isArray(details.accrual_transactions)
      ? details.accrual_transactions
      : [];
    const tag = details.tag;
    const hasUtr = Boolean(details.utr);
    const showPaidOn = isValidDate(details.paid_on);
    const isCarryForward = (tag || "").toLowerCase() === "carry forward";
    const showInfoCard = hasUtr || isCarryForward || history.length > 0;
    const dueDate = formatFullDate(details.due_date);

    const infoRows = [];
    if (hasUtr) {
      infoRows.push(
        <InfoRow key="utr">
          <span>UTR</span>
          <InfoValue>
            {details.utr}
            <CopyButton
              type="button"
              onClick={() => copyToClipboard(details.utr)}
              aria-label="Copy UTR"
            >
              <CopyIcon />
            </CopyButton>
          </InfoValue>
        </InfoRow>,
        <InfoRow key="total">
          <span>Total paid (in UTR)</span>
          <InfoValue>{formatMoney(details.utr_amount, { spaced: true })}</InfoValue>
        </InfoRow>
      );
    }
    if (showPaidOn) {
      infoRows.push(
        <InfoRow key="paid-on">
          <span>Paid on</span>
          <InfoValue>{formatFullDate(details.paid_on)}</InfoValue>
        </InfoRow>
      );
    }
    if (dueDate) {
      infoRows.push(
        <InfoRow key="due">
          <span>Due date</span>
          <InfoValue>{dueDate}</InfoValue>
        </InfoRow>
      );
    }

    return (
      <>
        {showInfoCard ? (
          <Card>
            {withDividers(infoRows)}
            {history.length ? (
              <>
                {infoRows.length ? <Divider /> : null}
                <HistoryTitle>History</HistoryTitle>
                {withDividers(historyEntries(history))}
              </>
            ) : null}
          </Card>
        ) : null}

        <SummaryCard>
          {tag ? (
            <TagRow>
              <Pill status={tag}>{tag}</Pill>
            </TagRow>
          ) : null}
          <SummaryHeader hasTag={Boolean(tag)}>
            <div>
              <SummaryDate>{dueDate}</SummaryDate>
              <SummaryCount>
                {transactions.length}{" "}
                {transactions.length === 1 ? "transaction" : "transactions"}
              </SummaryCount>
            </div>
            <SummaryAmount>{formatMoney(details.amount)}</SummaryAmount>
          </SummaryHeader>
          <TxnList>
            {withDividers(
              transactions.map((txn, idx) => (
                <TxnRow key={`${txn.transaction_type}-${txn.transaction_number}-${idx}`}>
                  <div>
                    <TxnTitle>{txn.transaction_type}</TxnTitle>
                    <TxnId>{txn.transaction_number}</TxnId>
                  </div>
                  <TxnAmount>
                    {formatMoney(txn.amount, { spaced: true })}
                  </TxnAmount>
                </TxnRow>
              ))
            )}
          </TxnList>
        </SummaryCard>
      </>
    );
  };

  return (
    <React.Fragment>
      <Header>
        <BackWrap>
          <BackButton />
        </BackWrap>
        <HeaderTitle>Payment details</HeaderTitle>
      </Header>
      <Page>{renderContent()}</Page>
    </React.Fragment>
  );
};

export default PaymentDetails;
